import classNames from "classnames"
import React, { useCallback } from "react"

import { Color } from "../../../constants/V2/color"
import { trackSegmentEvent } from "../../../utils/analytics"
import { getUrlFromStoryblokLink } from "../../../utils/storyblok"
import Link from "../../elements/Link"
import Typography from "../../elements/V2/Typography"

import TextButton from "@components/elements/V2/Buttons/TextButton"
import GatsbyStoryblokImage from "@components/elements/V2/GatsbyStoryblokImage"
import ArrowIcon from "@components/elements/V2/Icons/ArrowIcon"

interface LinksMenu {
  type: "link"
  title: string
  links: ImageLinks[]
  ctaText?: string
  ctaLink?: string
  queryParams?: string
}

interface DisplayLinkMenu {
  type: "display"
  title: string
  link: ImageLinks
  hoverZoom?: boolean
}

interface ImageLinks {
  label: string
  description: string
  link: string | Storyblok.Link
  image?: string
  imageSize?: "small" | "large"
  queryParams?: string
  badgeText?: string
  badgeColor?: Color
}

export type SubMenuBlockProps = LinksMenu | DisplayLinkMenu

type Props = SubMenuBlockProps & {
  className?: string
  onClick?: () => void
}

const SubMenuBlock = ({ className, onClick, ...props }: Props) => {
  const animateClassnames =
    "transition-all duration-200 lg:translate-x-[-20px] lg:group-hover:translate-x-0"

  const handleClick = useCallback(
    // eslint-disable-next-line @typescript-eslint/member-delimiter-style
    ({ label, link }: { label: string; link: string | Storyblok.Link }) => {
      trackSegmentEvent("main_navigation_link", {
        anchorText: label,
        path: typeof link === "string" ? link : getUrlFromStoryblokLink(link),
      })
      onClick?.()
    },
    [onClick]
  )

  return (
    <div className={classNames(className, "col-span-4 pt-[6px] sm:pt-0")}>
      <Typography
        color={Color.Charcoal}
        font="grotesk"
        weight="book"
        size="subscript-sm"
        className="mb-16 uppercase tracking-wide"
        text={props.title}
      />

      {props.type === "link" ? (
        <div className="flex flex-col gap-[12px]">
          {props.links.map((link, index) => {
            return (
              <Link
                key={`link-${index}`}
                link={link.link}
                queryParams={link.queryParams}
                onClick={() =>
                  handleClick({ label: link.label, link: link.link })
                }
                className={classNames(
                  link.imageSize === "small" ? "items-start" : "items-center",
                  "group flex w-fit gap-10"
                )}
              >
                {link.image && (
                  <GatsbyStoryblokImage
                    aspectRatio="1:1"
                    image={link.image}
                    alt={link.label}
                    className={classNames(
                      link.imageSize === "small"
                        ? "h-[24px] w-[24px]"
                        : "h-[42px] w-[42px]",
                      "rounded-[4px] lg:max-h-104"
                    )}
                  />
                )}
                <div className="flex flex-col gap-[2px]">
                  <div className="flex items-center gap-[5px]">
                    <Typography
                      color={Color.Charcoal}
                      font="grotesk"
                      weight="medium"
                      size="body-md-fixed"
                      text={link.label}
                      className={classNames(
                        "duration-200 ease-in-out group-hover:text-blue"
                      )}
                    />
                    <ArrowIcon
                      className={classNames(
                        "-mt-[2px] group-hover:text-blue lg:opacity-0 lg:group-hover:opacity-100",
                        animateClassnames
                      )}
                      width="12"
                      height="12"
                    />
                    {link.badgeText && link.badgeColor && (
                      <Typography
                        color={link.badgeColor || Color.Charcoal}
                        font="grotesk"
                        weight="book"
                        size="subscript-md"
                        text={link.badgeText}
                        className={classNames(
                          "ml-4 uppercase",
                          animateClassnames
                        )}
                      />
                    )}
                  </div>
                  <Typography
                    color={Color.Charcoal}
                    font="grotesk"
                    weight="book"
                    size="subscript-sm"
                    text={link.description}
                    className={"opacity-70"}
                  />
                </div>
              </Link>
            )
          })}

          {props.ctaLink && props.ctaText && (
            <TextButton
              text={props.ctaText}
              style="icon"
              linkUrl={props.ctaLink}
              queryParams={props.queryParams}
              icon={<ArrowIcon width="12" height="10" />}
              color={Color.Black}
              textSize="body-md-fixed"
              className="mt-8"
            />
          )}
        </div>
      ) : props.type === "display" ? (
        <Link
          link={props.link.link}
          queryParams={props.link.queryParams}
          onClick={() =>
            handleClick({ label: props.link.label, link: props.link.link })
          }
          className="group flex w-fit items-center gap-20 lg:flex-col lg:items-start"
        >
          {props.link.image && (
            <div className="min-w-min overflow-hidden rounded-[4px]">
              <GatsbyStoryblokImage
                image={props.link.image}
                alt={props.link.label}
                aspectRatio="16:9"
                className={classNames(
                  {
                    "transform transition-transform duration-300 group-hover:scale-110":
                      props.hoverZoom,
                  },
                  "h-[57px] w-[74px] lg:h-104 lg:w-[180px]"
                )}
              />
            </div>
          )}
          <div className="flex h-[57px] max-w-[200px] flex-col justify-between gap-1 lg:h-auto lg:flex-col-reverse lg:justify-normal">
            <Typography
              color={Color.Charcoal}
              font="grotesk"
              weight="book"
              size="subscript-sm"
              text={props.link.description}
              className="opacity-70"
            />
            <div className="flex items-center gap-[5px]">
              <Typography
                color={Color.Charcoal}
                font="grotesk"
                weight="medium"
                size="body-md-fixed"
                text={props.link.label}
                className={classNames(
                  "relative duration-200 ease-in-out group-hover:text-blue"
                )}
              />
              <ArrowIcon
                className={classNames(
                  "-mt-[2px] group-hover:text-blue lg:opacity-0 lg:group-hover:opacity-100",
                  animateClassnames
                )}
                width="12"
                height="10"
              />
            </div>
          </div>
        </Link>
      ) : null}
    </div>
  )
}

export default SubMenuBlock
